import {
  Box,
  Grid,
  Toolbar,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Pagination,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { CloudDownloadOutlined, KeyboardArrowLeftOutlined } from "@mui/icons-material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useNavigate } from "react-router-dom";

function StockTransactionsReport() {
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("role");
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");
  const navigate = useNavigate();

  // Filters
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [selectedStorageLocations, setSelectedStorageLocations] = useState("");

  // UI
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  // DatePicker expects Date objects ✅
  const [selectedStartDate, setSelectedStartDate] = useState(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(new Date());

  // Data
  const [rawMaterials, setRawMaterials] = useState([]); // transaction rows
  const [categories, setCategories] = useState([]); // from current stock API in your code
  const [storageLocations, setStorageLocations] = useState([]);

  // Pagination
  const itemsPerPage = 50;
  const [currentPage, setCurrentPage] = useState(1);

  const getRawMaterialsList = async () => {
    if (!outletData?._id) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock?outletId=${outletData._id}&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCategories(response?.data?.data?.rawMaterialCategories || []);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to fetch categories", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const getStorageLocation = async () => {
    try {
      const response = await axios.get(`${API.BASE_URL}storage-location`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStorageLocations(response?.data?.data?.storageLocations || []);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to fetch storage locations", { autoClose: 3000 });
    }
  };

  const getStockTransactions = async (download = false) => {
    if (!outletData?._id) return;

    if (selectedStartDate && selectedEndDate && selectedStartDate > selectedEndDate) {
      toast.error("Start Date cannot be greater than End Date", { autoClose: 3000 });
      return;
    }

    setLoading(true);
    try {
      const fromDate = format(selectedStartDate, "yyyy-MM-dd");
      const toDate = format(selectedEndDate, "yyyy-MM-dd");

      const params = {
        fromDate,
        toDate,
      };

      if (selectedCategory) params.mainCategory = selectedCategory;
      if (selectedMaterial) params.rawMaterialId = selectedMaterial;
      if (selectedStorageLocations) params.storageLocation = selectedStorageLocations;

      // ✅ Tell backend this is a download request (same idea as your getProductSales)
      if (download) params.isDownload = "true";

      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletStockTransactions?outletId=${outletData?._id}&limit=1000`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
          responseType: download ? "blob" : "json",
        }
      );

      // ✅ Download flow
      if (download) {
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);

        const selectedOutletName = outletData?.name || outletData?.outletName || "Outlet";
        const fileName = `${selectedOutletName} Stock Transactions Report (${fromDate} to ${toDate}).xlsx`;

        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);

        toast.success("Report Excel file downloaded successfully!", {
          position: "bottom-center",
          autoClose: 3000,
        });
        return;
      }

      // ✅ Preview flow (your existing logic)
      const records = response?.data?.data?.outletStockTransactionRecords;

      if (typeof records === "string" && records.includes("No Records")) {
        setRawMaterials([]);
      } else {
        setRawMaterials(Array.isArray(records) ? records : []);
      }

      setCurrentPage(1);
    } catch (error) {
      console.error("Error:", error);
      toast.error(download ? "Failed to download report" : "Failed to fetch report", {
        autoClose: 3000,
      });
      if (!download) setRawMaterials([]);
    } finally {
      setLoading(false);
    }
  };

  const uniqueCategories = useMemo(() => {
    const arr = Array.isArray(categories) ? categories : [];
    return arr.filter(
      (category, index, self) =>
        index === self.findIndex((c) => c?.mainCategory?._id === category?.mainCategory?._id)
    );
  }, [categories]);

  const uniqueRawMaterials = useMemo(() => {
    const arr = Array.isArray(rawMaterials) ? rawMaterials : [];
    return arr.filter(
      (material, index, self) =>
        index === self.findIndex((m) => m?.rawMaterial?._id === material?.rawMaterial?._id)
    );
  }, [rawMaterials]);

  const filteredRows = useMemo(() => {
    const rows = Array.isArray(rawMaterials) ? rawMaterials : [];
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const name = (r?.rawMaterial?.name || "").toLowerCase();
      const cat = (r?.rawMaterial?.mainCategory?.name || "").toLowerCase();
      const sub = (r?.rawMaterial?.subCategory?.name || "").toLowerCase();
      const type = (r?.type || "").toLowerCase();
      const remark = (r?.remark || "").toLowerCase();
      const storage = (r?.storageLocationDetails?.name || "").toLowerCase();
      return (
        name.includes(q) ||
        cat.includes(q) ||
        sub.includes(q) ||
        type.includes(q) ||
        remark.includes(q) ||
        storage.includes(q)
      );
    });
  }, [rawMaterials, searchText]);

  const totalPages = Math.ceil(filteredRows.length / itemsPerPage) || 1;

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, currentPage]);

  const handlePageChange = (event, page) => setCurrentPage(page);

  useEffect(() => {
    getRawMaterialsList();
    getStorageLocation();
  }, []);

  return (
    <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
      <Toolbar />

      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={11}>
          {/* Header */}
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack flexDirection={'row'}>
                  <KeyboardArrowLeftOutlined sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)} fontSize="large" />
                  <Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                      Stock Transactions Reports
                    </Typography>
                    <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                      Filter by date range, category, material, and storage location. View or download the report.
                    </Typography>
                  </Box>
                </Stack>

                <Chip
                  icon={<FilterAltOutlined />}
                  label={`Records: ${filteredRows?.length || 0}`}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "#fff",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={2}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    From Date
                  </Typography>

                  <DatePicker
                    selected={selectedStartDate}
                    onChange={(date) => setSelectedStartDate(date)}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={
                      <TextField
                        fullWidth
                        value={selectedStartDate ? format(selectedStartDate, "yyyy-MM-dd") : ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                            background: "#fff",
                          },
                        }}
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    To Date
                  </Typography>

                  <DatePicker
                    selected={selectedEndDate}
                    onChange={(date) => setSelectedEndDate(date)}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={
                      <TextField
                        fullWidth
                        value={selectedEndDate ? format(selectedEndDate, "yyyy-MM-dd") : ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                            background: "#fff",
                          },
                        }}
                      />
                    }
                  />
                </Grid>

                <Grid item xs={12} md={2.5}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Storage Location
                  </Typography>

                  <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 } }}>
                    <Select value={selectedStorageLocations} onChange={(e) => setSelectedStorageLocations(e.target.value)} displayEmpty>
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>
                      {storageLocations?.map((loc) => (
                        <MenuItem key={loc?._id} value={loc?._id}>
                          {loc?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2.5}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Category
                  </Typography>

                  <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 } }}>
                    <Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} displayEmpty>
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>

                      {uniqueCategories?.map((category) => (
                        <MenuItem key={category?.mainCategory?._id} value={category?.mainCategory?._id}>
                          {category?.mainCategory?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Raw Material
                  </Typography>

                  <FormControl fullWidth sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 } }}>
                    <Select value={selectedMaterial} onChange={(e) => setSelectedMaterial(e.target.value)} displayEmpty>
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>

                      {uniqueRawMaterials?.map((m) => (
                        <MenuItem key={m?.rawMaterial?._id} value={m?.rawMaterial?._id}>
                          <span style={{ textTransform: "capitalize" }}>{m?.rawMaterial?.name}</span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={5}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Search
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Search by material / category / type / remark"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52, background: "#fff" } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#334155",
                      mb: 0.8,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Typography>

                  <Stack direction="row" justifyContent={"space-between"} spacing={1}>
                    <Button
                      variant="contained"
                      disabled={loading}
                      onClick={() => getStockTransactions(false)}
                      sx={{
                        height: 52,
                        width: "70%",
                        borderRadius: 2,
                        fontWeight: 800,
                        textTransform: "none",
                        backgroundColor: "#003049",
                        "&:hover": { backgroundColor: "#00263a" },
                      }}
                    >
                      {loading ? "Loading..." : "Get"}
                    </Button>

                    <CloudDownloadOutlined
                      onClick={() => {
                        if (!filteredRows?.length || loading) return;
                        getStockTransactions(true);
                      }}
                      sx={{
                        fontSize: "35px",
                        cursor: filteredRows?.length ? "pointer" : "not-allowed",
                        color: filteredRows?.length ? "#003049" : "#94a3b8",
                        display: "flex",
                        alignSelf: "center",
                      }}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Content */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <CircularProgress />
                  <Typography sx={{ color: "#334155", fontWeight: 700 }}>Fetching Report...</Typography>
                </Box>
              ) : filteredRows?.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                    No stock transactions to display
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Select filters and click “Get” to view results.
                  </Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap">
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          icon={<FilterAltOutlined />}
                          label={`Rows: ${filteredRows?.length || 0}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                        <Chip
                          label={`Date: ${format(selectedStartDate, "yyyy-MM-dd")} → ${format(selectedEndDate, "yyyy-MM-dd")}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                      </Stack>

                      <Chip
                        label={outletData?.name || "Outlet"}
                        sx={{
                          fontWeight: 900,
                          borderRadius: 2,
                          backgroundColor: "#003049",
                          color: "#fff",
                          "& .MuiChip-label": { color: "#fff" },
                        }}
                      />
                    </Stack>
                  </Box>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: 0,
                      maxHeight: "70vh",
                      overflow: "auto",
                    }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {["No.", "Date & Time", "Raw Material", "Storage Location", "Type", "Qty"].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 900,
                                color: "#0f172a",
                                background: "#f1f5f9",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                              }}
                              align={h === "Raw Material" ? "left" : "center"}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {paginatedRows.map((m, index) => (
                          <TableRow
                            key={m?._id || index}
                            hover
                            sx={{
                              backgroundColor: index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                              "& td": { borderBottom: "1px solid #f1f5f9" },
                            }}
                          >
                            <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </TableCell>

                            <TableCell align="center">
                              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                                {format(new Date(m?.createdAt), "yyyy-MM-dd")}
                              </Typography>
                              <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                {new Date(m?.createdAt).toLocaleTimeString("en-GB", {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit",
                                })}
                              </Typography>
                            </TableCell>

                            <TableCell align="left">
                              <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#0f172a", textTransform: "capitalize" }}>
                                {m?.rawMaterial?.name || "N/A"}
                              </Typography>

                              <Typography sx={{ fontSize: 12, color: "#64748b", textTransform: "capitalize" }}>
                                {m?.rawMaterial?.mainCategory?.name || "N/A"} • {m?.rawMaterial?.subCategory?.name || "N/A"}
                              </Typography>

                              {!!m?.remark && (
                                <Typography sx={{ fontSize: 11, color: "#475569", mt: 0.3, fontStyle: "italic" }}>
                                  {m?.remark}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                              {m?.storageLocationDetails
                                ? `${m?.storageLocationDetails?.name || ""}, ${m?.storageLocationDetails?.city || ""}, ${m?.storageLocationDetails?.state || ""}`
                                : "----"}
                            </TableCell>

                            <TableCell align="center" sx={{ fontWeight: 800 }}>
                              {m?.type || "-"}
                            </TableCell>

                            <TableCell align="center" sx={{ fontWeight: 900, color: "#0f172a" }}>
                              {Number(m?.quantity || 0).toFixed(2)}
                              {m?.rawMaterial?.unit || ""}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />

                  <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default StockTransactionsReport;
